import { Injectable } from '@angular/core';
import { DropboxService, UploadResult, DesignFile } from '../dropbox/dropbox.service';

@Injectable({
  providedIn: 'root'
})
export class UploadDesignService {

  constructor(private dropboxService: DropboxService) {
  }

  private generateUniqueId(): string {
    // Generate a unique ID using timestamp and random string
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 15);
    return `${timestamp}_${randomStr}`;
  }

  async uploadDesignFiles(designFiles: DesignFile[]): Promise<UploadResult> {
    try {
      // Upload files using DropboxService (it will handle token refresh automatically)
      const result = await this.dropboxService.uploadDesignFiles(designFiles);
      
      return result;
    } catch (error) {
      console.error('Error uploading design files:', error);
      throw error;
    }
  }

  // Backward compatibility method for files without position information
  async uploadDesignFilesLegacy(files: File[]): Promise<UploadResult> {
    try {
      // Convert files to DesignFile objects assuming order: Front, Back, Left, Right
      const designFiles: DesignFile[] = files.map((file, index) => {
        const positions: ('Front' | 'Back' | 'Left' | 'Right')[] = ['Front', 'Back', 'Left', 'Right'];
        return {
          file: file,
          position: positions[index] || 'Front'
        };
      });
      
      return await this.uploadDesignFilesWithProgress(designFiles);
    } catch (error) {
      console.error('Error uploading design files:', error);
      throw error;
    }
  }

  async uploadDesignFilesWithProgress(designFiles: DesignFile[], onProgress?: (progress: number) => void): Promise<UploadResult> {
    try {
      // Upload files using DropboxService (it will handle token refresh automatically)
      const result: UploadResult = {
        previewImages: [],
        destinationProductLinks: [],
        destinationProductLinkText: ""
      };

      const now = new Date();
      const dateStr = now.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: '2-digit' 
      }).replace(/\//g, ' ');
      const timeStr = now.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      }).replace(/:/g, '');

      // Initialize with 4 empty rows (Front, Back, Left, Right)
      const positionLinks = ['', '', '', ''];
      const positionOrder = { 'Front': 0, 'Back': 1, 'Left': 2, 'Right': 3 };

      // Create upload promises for parallel processing
      const uploadPromises = designFiles.map(async (designFile, index) => {
        const file = designFile.file;
        const position = designFile.position;
        
        try {
          // Generate unique ID for this file
          const uniqueId = this.generateUniqueId();
          const fileExtension = file.name.split('.').pop();
          const baseFileName = file.name.replace(/\.[^/.]+$/, "");

          // Upload original file with unique ID
          const remotePath = `/UploadDesignHere/${dateStr} ${timeStr}/${uniqueId}_${baseFileName}.${fileExtension}`;
          const uploadResult = await this.dropboxService.uploadFile(remotePath, file);
          const desLink = await this.dropboxService.createSharedLink(uploadResult.path_lower);
          
          // Return result with position info
          return {
            desLink,
            position,
            positionIndex: positionOrder[position],
            index
          };
          
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          throw error;
        }
      });

      // Wait for all uploads to complete in parallel
      const uploadResults = await Promise.all(uploadPromises);
      
      // Process results and populate arrays
      uploadResults.forEach((uploadResult) => {
        result.previewImages.push(uploadResult.desLink);
        result.destinationProductLinks.push(uploadResult.desLink);
        
        // Add to position-specific array based on position
        positionLinks[uploadResult.positionIndex] = uploadResult.desLink;
      });

      // Final progress update
      if (onProgress) {
        onProgress(100);
      }

      // Create the structured link text with 4 rows (Front, Back, Left, Right)
      result.destinationProductLinkText = positionLinks.join("\n");
      return result;
    } catch (error) {
      console.error('Error uploading design files:', error);
      throw error;
    }
  }

  private async createThumbnail(file: File, width: number, height: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = width;
        canvas.height = height;
        
        // Calculate scaling to maintain aspect ratio
        const scale = Math.min(width / img.width, height / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        
        // Center the image
        const x = (width - scaledWidth) / 2;
        const y = (height - scaledHeight) / 2;
        
        ctx?.drawImage(img, x, y, scaledWidth, scaledHeight);
        
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create thumbnail'));
          }
        }, 'image/jpeg', 0.9);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

}
